// src/receptionist/bookings.ts
//
// The booking queue and the conversation behind each one. The transcript is
// the point: a merchant judging whether to trust the AI reads the thread, not
// the row. Fixture data until the BE contract lands.

export type BookingStatus = "Pending" | "Confirmed" | "Declined" | "Cancelled";
export type TurnAuthor = "customer" | "ai" | "system";

/** One attachment on a turn, a link that opens now (ourteam-backend#6843). */
export interface TurnMedia {
    url: string;
    /** "image/jpeg", "application/pdf"…; a service's picture is the bare "image". */
    contentType: string;
}

export interface Turn {
    who: TurnAuthor;
    text: string;
    /** The message's attachments; absent from an older backend and on text-only turns. */
    media?: TurnMedia[];
    /** When it was sent, ISO Z (ourteam-backend#6997); absent from an older backend. */
    at?: string | null;
}

/** A question the booking's steps asked, and the answer (ourteam-backend#6997). */
export interface BookingAnswer {
    section: string;
    question: string;
    answer: string;
    /** The answer tripped a screening gate. */
    flagged: boolean;
}

/** A document the booking's steps ask for, and whether it arrived. */
export interface BookingDocument {
    label: string;
    received: boolean;
}

/** The placeholder the backend logs for a message that was only an attachment. */
export const MEDIA_PLACEHOLDER = "[media]";

export function isImage(m: TurnMedia): boolean {
    const t = (m.contentType || "").toLowerCase();
    return t === "image" || t.startsWith("image/");
}

export interface Booking {
    id: string;
    customer: string;
    phone: string;
    service: string;
    /** The customer's own words for the slot ("tomorrow at 3PM"), never rewritten. */
    slot: string;
    /**
     * The same moment as stored (`rcp_bookings.slot_at`, TIMESTAMPTZ), ISO Z; null
     * when the parser could not pin one down. This is what the console shows
     * (#4798): "tomorrow" is only true on the day it was said.
     */
    slotAt?: string | null;
    /** How it arrived: "Chat", "After-hours chat", "Reschedule in chat". */
    source: string;
    status: BookingStatus;
    /** When the request arrived (ourteam-backend#6718); absent from an older server. */
    createdAt?: string | null;
    /** Short outcome line shown once the booking is no longer pending. */
    note?: string;
    /** Behaviours worth surfacing on the row: voice note, mixed language, etc. */
    tags: string[];
    /** What the steps asked and what the customer said; absent from an older backend. */
    answers?: BookingAnswer[];
    documents?: BookingDocument[];
    transcript: Turn[];
    /**
     * ISO timestamp of the customer's most recent inbound message, used to work
     * out whether WhatsApp's 24-hour free-form window is still open.
     *
     * Live on `GET .../bookings` since ourteam-backend#4669 shipped; it was a
     * proposed field when this was written. Absent is still treated as "window
     * closed", so an older backend leaves the composer disabled rather than
     * letting a send fail. The server's own 409 remains the authority.
     */
    lastCustomerMessageAt?: string;
}

/**
 * WhatsApp only delivers a free-form (non-template) message within 24 hours of
 * the customer's last inbound one. This is the proactive hint; the server's
 * refusal is still the real check.
 */
export const WHATSAPP_FREEFORM_WINDOW_MS = 24 * 60 * 60 * 1000;

export function isReplyWindowOpen(booking: Pick<Booking, "lastCustomerMessageAt">): boolean {
    if (!booking.lastCustomerMessageAt) return false;
    const at = new Date(booking.lastCustomerMessageAt).getTime();
    if (Number.isNaN(at)) return false;
    return Date.now() - at < WHATSAPP_FREEFORM_WINDOW_MS;
}

/** Relative to now, so the demo window never goes stale. */
function hoursAgo(h: number): string {
    return new Date(Date.now() - h * 60 * 60 * 1000).toISOString();
}

export function demoBookings(): Booking[] {
    return [
        {
            id: "b1", customer: "Mei Ling", phone: "012-XXX XXXX",
            service: "Signature Deep Cleansing Facial", slot: "Fri 4 Sept, 3.00pm",
            source: "Chat", status: "Confirmed", note: "Confirmed by Aina, 4.09pm", tags: [],
            lastCustomerMessageAt: hoursAgo(30),
            transcript: [
                { who: "customer", text: "Can I book a facial this friday?" },
                { who: "ai", text: "Of course. Which treatment would you like? The most popular are the Signature Facial (RM268) and the Hydra Glow (RM328)." },
                { who: "customer", text: "signature one. friday 3pm, name is Mei Ling" },
                { who: "ai", text: "Thank you, Mei Ling. To confirm your request:\n\nSignature Facial\nFriday 4 Sept, 3.00pm\nUnder: Mei Ling\n\nCorrect?" },
                { who: "customer", text: "correct" },
                { who: "ai", text: "Request received. Our team will confirm your slot shortly and message you here." },
                { who: "system", text: "Aina confirmed in the staff group at 4.09pm" },
                { who: "ai", text: "Good news, Mei Ling: your Signature Facial on Friday 4 Sept at 3.00pm is confirmed. See you then!" },
            ],
        },
        {
            id: "b2", customer: "Nurul Ain", phone: "013-XXX XXXX",
            service: "Hydra Glow Facial", slot: "Sat 5 Sept, 11.00am",
            source: "After-hours chat", status: "Pending", tags: ["After hours", "Mixed BM and English"],
            lastCustomerMessageAt: hoursAgo(2),
            transcript: [
                { who: "system", text: "Received 11.42pm, shop closed" },
                { who: "customer", text: "hi masih buka tak? nak book facial" },
                { who: "ai", text: "Hi! Kami dah tutup sekarang, buka esok pukul 10.00 pagi. Saya boleh ambil booking sekarang dan team akan confirm bila buka. Facial yang mana ya?" },
                { who: "customer", text: "yang hydra glow tu. sabtu pagi boleh? nama Nurul Ain" },
                { who: "ai", text: "Noted, Nurul Ain. Hydra Glow Facial, Sabtu 5 Sept, 11.00 pagi. RM328, first visit RM248. Team akan confirm pagi esok. Terima kasih!" },
            ],
        },
        {
            id: "b3", customer: "Priya", phone: "017-XXX XXXX",
            service: "Chemical Peel", slot: "Wed 9 Sept, 11.00am",
            source: "Chat", status: "Pending", tags: ["Escalated first"],
            lastCustomerMessageAt: hoursAgo(6),
            transcript: [
                { who: "customer", text: "Hi, I have eczema on my cheeks. Is the chemical peel safe for me?" },
                { who: "ai", text: "That is a question our therapist should answer personally, as it depends on your skin condition. I have passed it to the team and they will reply to you here shortly." },
                { who: "system", text: "Escalated to Aina. Human replied in 14 minutes, the AI stood down in this thread" },
                { who: "ai", text: "Aina here. Our therapist says a gentle peel is fine as long as the eczema is not active. Come in and we will patch test first." },
                { who: "customer", text: "ok great. can i book wednesday 11am? Priya" },
                { who: "system", text: "Handed back to the AI with #back" },
                { who: "ai", text: "Of course. Chemical Peel, Wed 9 Sept, 11.00am, under Priya. Our team will confirm shortly." },
            ],
        },
        {
            id: "b4", customer: "Wong Li Fen", phone: "012-XXX XXXX",
            service: "Signature Deep Cleansing Facial", slot: "Tue 8 Sept, 4.30pm",
            source: "Chat", status: "Confirmed", note: "Confirmed by front desk", tags: ["Voice note"],
            transcript: [
                { who: "customer", text: "[Voice note, 0:14]" },
                { who: "ai", text: "Got it, thank you. To confirm what I heard:\n\nSignature Deep Cleansing Facial\nTuesday 8 Sept, 4.30pm\nUnder: Li Fen\n\nHave I got that right?" },
                { who: "customer", text: "yes correct" },
                { who: "ai", text: "Request received. Our team will confirm your slot shortly." },
                { who: "system", text: "Confirmed in the staff group" },
            ],
        },
        {
            id: "b5", customer: "Sarah", phone: "019-XXX XXXX",
            service: "Signature Facial with LED add-on", slot: "Mon 7 Sept, 6.00pm",
            source: "Chat", status: "Pending", tags: ["Went quiet, nudged"],
            lastCustomerMessageAt: hoursAgo(20),
            transcript: [
                { who: "customer", text: "how much for signature facial + LED?" },
                { who: "ai", text: "The Signature Deep Cleansing Facial is RM268 for 90 minutes, and LED Therapy is RM88 as a 20-minute add-on. Would you like me to put a slot request in?" },
                { who: "customer", text: "let me check my schedule" },
                { who: "system", text: "No reply for 3 hours. One nudge sent inside the 24-hour window" },
                { who: "ai", text: "Hi Sarah, still keen on the Signature Facial with LED? Reply with a day and time and I will pass it to the team." },
                { who: "customer", text: "monday 6pm works. Sarah" },
                { who: "ai", text: "Noted. Signature Facial with LED add-on, Monday 7 Sept 6.00pm, under Sarah. The team will confirm shortly." },
            ],
        },
        {
            id: "b6", customer: "Tan Mei Kei", phone: "016-XXX XXXX",
            service: "Hydra Glow Facial", slot: "Sat 5 Sept, 2.00pm",
            source: "Chat", status: "Confirmed", note: "Confirmed by Aina", tags: ["Chinese"],
            transcript: [
                { who: "customer", text: "你好，请问补水面部护理多少钱？" },
                { who: "ai", text: "您好！补水焕采面部护理是 RM328，约 75 分钟。首次到店优惠价 RM248。需要我帮您留位吗？" },
                { who: "customer", text: "星期六下午两点可以吗？陈美琪" },
                { who: "ai", text: "好的，美琪。补水焕采面部护理，9月5日星期六下午2点。我们的同事会尽快跟您确认。" },
            ],
        },
        {
            id: "b7", customer: "Aina Rahman", phone: "011-XXX XXXX",
            service: "Signature Deep Cleansing Facial", slot: "Fri 4 Sept, 5.00pm",
            source: "Chat", status: "Declined", note: "Declined, fully booked. Alternatives offered", tags: ["No slot"],
            lastCustomerMessageAt: hoursAgo(40),
            transcript: [
                { who: "customer", text: "can i come friday 5pm for facial" },
                { who: "ai", text: "Let me take that down. Signature Deep Cleansing Facial, Friday 4 Sept 5.00pm, under Aina Rahman. Our team will confirm shortly." },
                { who: "system", text: "Declined in the staff group, 5.00pm is fully booked" },
                { who: "ai", text: "Sorry Aina, 5.00pm on Friday is fully booked. We have 2.00pm on Friday or 11.00am on Saturday. Would either of those work?" },
            ],
        },
        {
            id: "b8", customer: "Jia Hui", phone: "018-XXX XXXX",
            service: "Hydra Glow Facial", slot: "Thu 3 Sept, 2.00pm",
            source: "Chat", status: "Cancelled", note: "Cancelled by customer", tags: ["Policy quoted"],
            transcript: [
                { who: "customer", text: "hi i need to cancel my facial tomorrow" },
                { who: "ai", text: "You have a Hydra Glow Facial tomorrow, Thu 3 Sept at 2.00pm. One note on our policy: cancellations under 24 hours before the appointment may forfeit the booking fee. Shall I cancel it?" },
                { who: "customer", text: "yes please" },
                { who: "ai", text: "Cancelled, and I have let the team know. Hope to see you another time." },
            ],
        },
    ];
}

/** The merchant's wall clock: the backend's `merchant_tz()`, Malaysia for every merchant today. */
export const MERCHANT_TZ = "Asia/Kuala_Lumpur";

function parts(iso: string, opts: Intl.DateTimeFormatOptions): Record<string, string> {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return {};
    return Object.fromEntries(
        new Intl.DateTimeFormat("en-US", { timeZone: MERCHANT_TZ, ...opts }).formatToParts(d).map((p) => [p.type, p.value]),
    );
}

/**
 * The stored slot, for the list and the drawer: "Wed 9 Sep 2026" and "1:00 PM"
 * in the merchant's time zone, or null when there is no stored moment.
 */
export function slotWhen(b: Pick<Booking, "slotAt">): { date: string; time: string } | null {
    if (!b.slotAt) return null;
    const p = parts(b.slotAt, { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
    if (!p.day) return null;
    return { date: `${p.weekday} ${p.day} ${p.month} ${p.year}`, time: `${p.hour}:${p.minute} ${p.dayPeriod}` };
}

/**
 * `{date}` and `{time}` as the backend fills them (bookings.slot_parts):
 * "Wednesday 9 Sep" and "1.00pm" from the stored moment; else the agreed text
 * split on its comma.
 */
export function slotPlaceholders(b: Pick<Booking, "slot" | "slotAt">): { date: string; time: string } {
    if (b.slotAt) {
        const p = parts(b.slotAt, { weekday: "long", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
        if (p.day) return { date: `${p.weekday} ${p.day} ${p.month}`, time: `${p.hour}.${p.minute}${(p.dayPeriod ?? "").toLowerCase()}` };
    }
    const text = b.slot || "";
    if (text.includes(",")) {
        const [date, ...rest] = text.split(",");
        return { date: date.trim(), time: rest.join(",").trim() };
    }
    return { date: text.trim(), time: "" };
}
